import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import type { Account } from '../../types/account';

type Mode = 'cancel' | 'hard';

export type DeleteAccountModalProps = {
  mode: Mode;
  account: Account;
  childrenCount: number;
  contactsLinked: number;
  onClose: () => void;
  onConfirmCancel: () => Promise<void>;
  onConfirmHard: () => Promise<void>;
};

const CONFIRM_WORD = 'DELETE';

export function DeleteAccountModal({
  mode,
  account,
  childrenCount,
  contactsLinked,
  onClose,
  onConfirmCancel,
  onConfirmHard,
}: DeleteAccountModalProps) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === 'cancel') {
        await onConfirmCancel();
      } else {
        await onConfirmHard();
      }
    },
    onError: (e: Error) => setError(e.message),
  });

  // Esc to close -- standard modal behaviour.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !mutation.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mutation.isPending, onClose]);

  const confirmReady = typed === CONFIRM_WORD && !mutation.isPending;
  const title = mode === 'cancel' ? 'Cancel account?' : 'Delete permanently?';
  const actionLabel = mode === 'cancel' ? 'Cancel account' : 'Delete permanently';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
    >
      <div className="w-full max-w-lg rounded-md bg-white shadow-xl">
        <div className="p-6 space-y-4">
          <h2 id="delete-account-title" className="text-lg font-semibold text-slate-900">
            {title}
          </h2>

          <div className="space-y-2 text-sm text-slate-700">
            <p>
              <span className="font-medium">{account.name}</span>{' '}
              <span className="text-slate-500 font-mono">({account.slug})</span>
            </p>

            {mode === 'cancel' ? (
              <p>
                This sets the account's status to <span className="font-mono">cancelled</span>.
                The account and all linked data stay intact; you can reactivate later by editing
                the status back to <span className="font-mono">active</span>.
              </p>
            ) : (
              <>
                <p>
                  This <span className="font-semibold text-red-700">permanently</span> deletes the
                  account. The action cannot be undone.
                </p>
                <ul className="list-disc pl-5 text-slate-600">
                  <li>Deletes the attached Franchise profile (if any).</li>
                  <li>Deletes the attached Franchise location profile (if any).</li>
                  <li>
                    Removes this account from{' '}
                    <span className="font-semibold">{contactsLinked}</span>{' '}
                    {contactsLinked === 1 ? 'contact' : 'contacts'}' linked accounts.
                  </li>
                  <li>Writes an audit log entry with the full snapshot of what was deleted.</li>
                </ul>
                {childrenCount > 0 && (
                  <p className="text-red-700">
                    This account has {childrenCount} child{' '}
                    {childrenCount === 1 ? 'account' : 'accounts'}. Hard delete is blocked by the
                    server; reparent or delete them first.
                  </p>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Type <span className="font-mono">{CONFIRM_WORD}</span> to confirm
            </label>
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              disabled={mutation.isPending}
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4 bg-slate-50 rounded-b-md">
          <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Keep account
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={!confirmReady}
          >
            {mutation.isPending ? 'Working…' : actionLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
